import AXIOS_INSTANCE from '@/src/api/axiosInstance'
import { useImagingOrder } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { imagingOrderFormSchema, type ImagingOrderFormValues } from '@schema/imagingOrder.schema'
import { GeneralResMock } from '@type/general.type'
import type {
  ImagingOrderData,
  ImagingOrderIncludedOrder,
  ImagingOrderIncludedResource,
  ImagingOrderUpdatePayload
} from '@type/imaging_order.type'
import { LaboratoryResMock } from '@type/laboratory.type'
import type { OrderImagingIncluded } from '@type/order.type'
import { FormButton, Modal, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { useCallback, useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import type { GroupBase } from 'react-select'
import { buildImagingPlacedLines } from './ImagingDetails.helpers'

type Props = {
  onClose: () => void
  id: string
  defaultData?: ImagingOrderData
  included?: ImagingOrderIncludedResource[]
}

type ImagingStudyOption = { label: string; value: number | string }

async function searchImagings(inputValue: string): Promise<ImagingStudyOption[]> {
  const q = new URLSearchParams({
    'page[number]': '1',
    'page[size]': '50',
    sort_by: 'study_type',
    sort_dir: 'asc'
  })
  const trimmed = inputValue.trim()
  if (trimmed) q.set('query', trimmed)

  const { data } = await AXIOS_INSTANCE.get<GeneralResMock<LaboratoryResMock[]>>(`/imagings?${q.toString()}`)
  const rows = Array.isArray(data.data) ? data.data : []
  return rows.map(imaging => ({
    label: imaging.attributes.study_type || imaging.attributes.name,
    value: Number(imaging.id)
  }))
}

function studiesFromOrder(
  order: ImagingOrderData,
  included: ImagingOrderIncludedResource[] | undefined
): ImagingStudyOption[] {
  const lines = buildImagingPlacedLines(order, included)
  return lines.map(line => {
    if (!line.orderId) {
      return { label: line.label, value: line.label }
    }
    const child = included?.find((x): x is ImagingOrderIncludedOrder => x.type === 'order' && x.id === line.orderId)
    const orderable = child?.relationships?.orderable
    const imagingIdFromRel =
      orderable && typeof orderable === 'object' && 'data' in orderable
        ? (orderable.data as { id?: string } | null | undefined)?.id
        : undefined
    const imagingId =
      imagingIdFromRel != null
        ? Number(imagingIdFromRel)
        : child?.attributes.orderable_id != null
          ? Number(child.attributes.orderable_id)
          : NaN
    const imaging = Number.isFinite(imagingId)
      ? included?.find((x): x is OrderImagingIncluded => x.type === 'imaging' && String(x.id) === String(imagingId))
      : undefined
    const label = imaging?.attributes.study_type ?? line.label
    // Keep a resolvable catalog id; never use NaN (that would drop free-text on save as study_names: []).
    if (Number.isFinite(imagingId)) {
      return { label, value: imagingId }
    }
    return { label, value: label }
  })
}

const EditImagingOrder = ({ onClose, id, defaultData, included }: Props) => {
  const { singleImagingOrder, updateImagingOrder } = useImagingOrder({ id })
  const order = (singleImagingOrder?.data?.type === 'imaging_order' ? singleImagingOrder.data : defaultData) as
    | ImagingOrderData
    | undefined
  const resolvedIncluded = singleImagingOrder?.included ?? included

  const defaultStudies = useMemo(
    () => (order ? studiesFromOrder(order, resolvedIncluded) : []),
    [order, resolvedIncluded]
  )

  const methods = useForm<ImagingOrderFormValues>({
    defaultValues: { imaging_studies: [], note: order?.attributes.note ?? '' },
    resolver: yupResolver(imagingOrderFormSchema)
  })
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = methods

  useEffect(() => {
    if (!order) return
    reset({
      imaging_studies: defaultStudies,
      note: order.attributes.note ?? ''
    })
  }, [defaultStudies, order, reset])

  const loadImagingOptions = useCallback((inputValue: string) => searchImagings(inputValue), [])

  const onSubmit = async (form: ImagingOrderFormValues) => {
    if (!id) return
    if (!order?.attributes.study_names_editable) {
      showToast('Studies can no longer be edited on this order')
      return
    }

    const imaging_ids = form.imaging_studies
      .map(s => s.value)
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
    const study_names = form.imaging_studies
      .filter(s => typeof s.value === 'string')
      .map(s => String(s.label).trim())
      .filter(Boolean)

    const payload: ImagingOrderUpdatePayload = {
      data: {
        type: 'imaging_order',
        attributes: {
          imaging_ids,
          note: form.note?.trim() ?? '',
          study_names
        }
      }
    }

    try {
      await updateImagingOrder({ formVal: payload, id })
      showToast('Imaging studies updated')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Edit studies">
        <form className="flex min-w-[530px] flex-col gap-4 overflow-auto" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4 overflow-auto p-6">
            <Controller
              control={control}
              name="imaging_studies"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select<ImagingStudyOption, true, GroupBase<ImagingStudyOption>>
                  borderRadius={'15px'}
                  defaultOptions
                  error={error?.message}
                  getNewOptionData={(inputValue, optionLabel) => ({
                    label: String(optionLabel),
                    value: String(inputValue).trim()
                  })}
                  getOptionLabel={o => o.label}
                  getOptionValue={o => String(o.value)}
                  isAsync
                  isCreatable
                  isMulti
                  label="Study"
                  loadOptions={loadImagingOptions}
                  onChange={opts => onChange((opts as ImagingStudyOption[] | null) ?? [])}
                  placeholder="Search catalog or type a study name"
                  value={value as ImagingStudyOption[]}
                />
              )}
            />

            <TextAreaField label="Note to radiology" name="note" placeholder="Enter Note" />
          </div>
          <FormButton isLoading={isSubmitting} onClose={onClose} submit="Save" />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default EditImagingOrder
